import type { WidgetDefinition } from './types';
import { ScheduleWidget } from './ScheduleWidget';
import { RainForecastWidget } from './RainForecastWidget';
import { AlertsWidget } from './AlertsWidget';
import { AlertsWidgetSettings } from './AlertsWidgetSettings';
import { StationObservationsWidget } from './StationObservationsWidget';
import { StationObservationsSettings } from './StationObservationsSettings';
import { StationParameterCardWidget } from './StationParameterCardWidget';
import { StationParameterCardSettings } from './StationParameterCardSettings';
import { AllStationsComparisonWidget } from './AllStationsComparisonWidget';
import { ForecastChartWidget } from './ForecastChartWidget';
import { RadarMapWidget } from './RadarMapWidget';
import { RadarMapSettings } from './RadarMapSettings';
import { StationChartsWidget } from './StationChartsWidget';
import { StationChartsSettings } from './StationChartsSettings';

const registry = new Map<string, WidgetDefinition>();

export function registerWidget(definition: WidgetDefinition): void {
  registry.set(definition.type, definition);
}

export function getWidget(type: string): WidgetDefinition | undefined {
  return registry.get(type);
}

export function getAllWidgets(): WidgetDefinition[] {
  return Array.from(registry.values());
}

export function getWidgetsByCategory(category: WidgetDefinition['category']): WidgetDefinition[] {
  return getAllWidgets().filter((w) => w.category === category);
}

// ── Weather Widgets ──

registerWidget({
  type: 'rain-forecast',
  title: 'Rain Forecast',
  description: 'Latest active rain forecast with status, direction, speed, and timing',
  category: 'weather',
  icon: 'CloudOutlined',
  defaultSize: { width: 360, height: 300 },
  minSize: { width: 180, height: 200 },
  breakpoints: { compact: 250, medium: 400 },
  capabilities: {
    requiresEvent: true,
    canSyncToPlayer: true,
    requiresObservations: false,
  },
  component: RainForecastWidget,
});

registerWidget({
  type: 'station-observations',
  title: 'Station Observations',
  description: 'Live station readings with field icons, station selector, and player sync',
  category: 'weather',
  icon: 'EnvironmentOutlined',
  defaultSize: { width: 360, height: 340 },
  minSize: { width: 180, height: 200 },
  breakpoints: { compact: 250, medium: 400 },
  capabilities: {
    requiresEvent: true,
    canSyncToPlayer: true,
    requiresObservations: true,
  },
  component: StationObservationsWidget,
  settingsComponent: StationObservationsSettings,
});

registerWidget({
  type: 'station-parameter-card',
  title: 'Station Parameter Card',
  description: 'Single weather parameter card with field icon, station or average display, and responsive sizing',
  category: 'weather',
  icon: 'DashboardOutlined',
  defaultSize: { width: 160, height: 84 },
  minSize: { width: 100, height: 56 },
  breakpoints: { compact: 120, medium: 180 },
  capabilities: {
    requiresEvent: true,
    canSyncToPlayer: true,
    requiresObservations: true,
  },
  component: StationParameterCardWidget,
  settingsComponent: StationParameterCardSettings,
  bodyOverflow: 'hidden',
});

registerWidget({
  type: 'all-stations-comparison',
  title: 'All Stations Comparison',
  description: 'Multi-station time-series comparison chart with parameter selection, averaging, and wind arrows',
  category: 'weather',
  icon: 'LineChartOutlined',
  defaultSize: { width: 700, height: 450 },
  minSize: { width: 250, height: 200 },
  breakpoints: { compact: 400, medium: 650 },
  capabilities: {
    requiresEvent: true,
    canSyncToPlayer: true,
    requiresObservations: true,
  },
  component: AllStationsComparisonWidget,
  hasInternalSettings: true,
});

registerWidget({
  type: 'forecast-chart',
  title: 'Forecast Chart',
  description: 'Daily or hourly forecast chart with parameter selection, synced to the dashboard timeline and player',
  category: 'weather',
  icon: 'AreaChartOutlined',
  defaultSize: { width: 700, height: 450 },
  minSize: { width: 250, height: 200 },
  breakpoints: { compact: 400, medium: 650 },
  capabilities: {
    requiresEvent: true,
    canSyncToPlayer: true,
    requiresObservations: false,
  },
  component: ForecastChartWidget,
  hasInternalSettings: true,
});

registerWidget({
  type: 'station-charts',
  title: 'Single Station Charts',
  description: 'Single-station weather charts with selectable chart types (temperature, wind, humidity/pressure, rain, wind direction)',
  category: 'weather',
  icon: 'DashboardOutlined',
  defaultSize: { width: 900, height: 500 },
  minSize: { width: 250, height: 200 },
  breakpoints: { compact: 400, medium: 700 },
  capabilities: {
    requiresEvent: true,
    canSyncToPlayer: true,
    requiresObservations: true,
  },
  component: StationChartsWidget,
  settingsComponent: StationChartsSettings,
});

// ── Operations Widgets ──

registerWidget({
  type: 'schedule',
  title: 'Session Schedule',
  description: 'Event session schedule with live session indicator and local times',
  category: 'ops',
  icon: 'CalendarOutlined',
  defaultSize: { width: 280, height: 400 },
  minSize: { width: 180, height: 200 },
  breakpoints: { compact: 220, medium: 350 },
  capabilities: {
    requiresEvent: true,
    canSyncToPlayer: false,
    requiresObservations: false,
  },
  component: ScheduleWidget,
});

registerWidget({
  type: 'alerts',
  title: 'Alerts',
  description: 'Event alerts with severity, type, and timing — configurable to show active, latest, or both',
  category: 'ops',
  icon: 'AlertOutlined',
  defaultSize: { width: 540, height: 400 },
  minSize: { width: 280, height: 200 },
  breakpoints: { compact: 300, medium: 500 },
  capabilities: {
    requiresEvent: true,
    canSyncToPlayer: false,
    requiresObservations: false,
  },
  component: AlertsWidget,
  settingsComponent: AlertsWidgetSettings,
});

// ── Map Widgets ──

registerWidget({
  type: 'radar-map',
  title: 'Radar Map',
  description: 'Interactive radar map with configurable source, layers, and overlays',
  category: 'map',
  icon: 'RadarChartOutlined',
  defaultSize: { width: 600, height: 500 },
  minSize: { width: 400, height: 300 },
  breakpoints: { compact: 450, medium: 600 },
  capabilities: {
    requiresEvent: true,
    canSyncToPlayer: true,
    requiresObservations: true,
  },
  component: RadarMapWidget,
  settingsComponent: RadarMapSettings,
});

